import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Calendar, Plus, Loader2, Tag, MapPin, Paperclip, Users, Info, Trash2, Settings, Building2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { otherCrmCreateMeeting, otherCrmUploadMinute, otherCrmListSummary } from '../../../../services/otherCRMService';
import { getCategories, addCategory, deleteCategory, getCustomCategories } from '../../../../services/meetingCategoriesService';

import TooltipHelper from '../../../../components/common/TooltipHelper';

/** Chips simple para strings */
function ChipsInput({ icon, placeholder = 'Escribe y Enter', values, onChange, hint }) {
  const [txt, setTxt] = useState('');
  const add = (raw) => {
    const v = String(raw || '').trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    onChange([...values, v]);
    setTxt('');
  };
  const removeAt = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
        {icon} {placeholder.includes('Enter') ? placeholder.replace('... (Enter)', '') : placeholder}
        {hint && <TooltipHelper text={hint} />}
      </label>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-white px-2 py-2 focus-within:ring-2 focus-within:ring-[#0488AF]">
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border bg-gray-100 text-gray-700">
            {v}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="w-5 h-5 grid place-items-center rounded-full hover:bg-gray-200"
              aria-label="Quitar"
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[180px] border-0 outline-none bg-transparent py-1 px-2 text-sm"
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(txt); } }}
          onBlur={() => { if (txt.trim()) add(txt); }}
          placeholder="Escribe el nombre y presiona Enter"
        />
      </div>
    </div>
  );
}

export default function QuickCreateMeetingModalExternal({ open, onClose, onCreated, onOpenCreateEntity }) {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entitySearch, setEntitySearch] = useState('');
  const [entityOptions, setEntityOptions] = useState([]);
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // chips de reunión
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [meetingTags, setMeetingTags] = useState([]);

  // Categorías (iniciales + personalizadas)
  const [categories, setCategories] = useState(getCategories());
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // Cargar categorías al montar y cuando cambien en localStorage
  useEffect(() => {
    const loadCategories = () => {
      setCategories(getCategories());
    };
    
    loadCategories();
    
    // Escuchar cambios en localStorage (para sincronizar entre pestañas/componentes)
    const handleStorageChange = (e) => {
      if (e.key === 'crm_meeting_categories') {
        loadCategories();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // También escuchar eventos personalizados para cambios en la misma pestaña
    window.addEventListener('categoriesUpdated', loadCategories);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('categoriesUpdated', loadCategories);
    };
  }, []);

  // Cargar entidades para autocompletar
  useEffect(() => {
    if (!open) return;
    
    const loadEntities = async () => {
      try {
        const list = await otherCrmListSummary(undefined);
        const entities = (Array.isArray(list) ? list : []).map(c => ({
          id: c.id ?? c.uuid ?? c._id ?? null,
          name: c.trade_name?.trim() ? c.trade_name : (c.business_name || c.name || 'Sin nombre'),
          business_name: c.business_name ?? '',
          trade_name: c.trade_name ?? '',
          region: c.region ?? '',
          district: c.district ?? '',
        })).filter(c => c.id && c.name !== 'Sin nombre');
        
        setEntityOptions(entities);
      } catch (e) {
        console.error('Error loading entities:', e);
        setEntityOptions([]);
      }
    };

    loadEntities();
  }, [open]);

  // Filtrar entidades según búsqueda
  const filteredEntities = useMemo(() => {
    if (!entitySearch.trim()) return entityOptions.slice(0, 10);
    const searchLower = entitySearch.toLowerCase();
    return entityOptions.filter(c => 
      c.name.toLowerCase().includes(searchLower) ||
      c.business_name?.toLowerCase().includes(searchLower) ||
      c.region?.toLowerCase().includes(searchLower) ||
      c.district?.toLowerCase().includes(searchLower)
    ).slice(0, 10);
  }, [entitySearch, entityOptions]);

  // Manejar clic fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowEntityDropdown(false);
      }
    };

    if (showEntityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEntityDropdown]);

  const handleSelectEntity = (entity) => {
    setSelectedEntity(entity);
    setEntitySearch(entity.name);
    setShowEntityDropdown(false);
  };

  const handleAddCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      Swal.fire('Campo vacío', 'Ingresa un nombre para la categoría', 'info');
      return;
    }
    
    try {
      addCategory(trimmed);
      setCategories(getCategories());
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      
      // Disparar evento para actualizar otros componentes
      window.dispatchEvent(new Event('categoriesUpdated'));
      
      Swal.fire('Categoría agregada', 'La categoría se ha agregado correctamente', 'success');
      // Cerrar el modal después de agregar
      setShowCategoriesModal(false);
    } catch (err) {
      Swal.fire('Error', err.message || 'No se pudo agregar la categoría', 'error');
    }
  };

  const handleDeleteCategory = async (categoryValue) => {
    const category = categories.find(c => c.value === categoryValue);
    const categoryName = category?.label || categoryValue;
    
    // Verificar si es la última categoría
    const allCategories = getCategories();
    if (allCategories.length <= 1) {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede eliminar',
        html: 'No se puede eliminar la última categoría. Crea una nueva categoría antes de eliminar esta.<br/><br/><strong>Nota:</strong> Las reuniones que usan esta categoría quedarán sin categoría asignada.',
        confirmButtonColor: '#0488AF',
      });
      return;
    }
    
    const result = await Swal.fire({
      title: '¿Eliminar categoría?',
      html: `¿Estás seguro de eliminar la categoría "${categoryName}"?<br/><br/><strong>Nota:</strong> Las reuniones que usan esta categoría quedarán sin categoría asignada. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        // Si la categoría a eliminar está seleccionada en el formulario, cambiarla a la primera disponible
        const form = document.querySelector('form');
        if (form) {
          const categorySelect = form.querySelector('select[name="category"]');
          if (categorySelect && categorySelect.value === categoryValue) {
            const remainingCategories = getCategories().filter(c => c.value !== categoryValue);
            if (remainingCategories.length > 0) {
              categorySelect.value = remainingCategories[0].value;
            }
          }
        }
        
        deleteCategory(categoryValue);
        setCategories(getCategories());
        
        // Disparar evento para actualizar otros componentes
        window.dispatchEvent(new Event('categoriesUpdated'));
        
        Swal.fire('Eliminada', `La categoría "${categoryName}" ha sido eliminada. Las reuniones que la usaban ahora están sin categoría asignada.`, 'success');
        // Cerrar el modal después de eliminar
        setShowCategoriesModal(false);
      } catch (err) {
        Swal.fire('Error', err.message || 'No se pudo eliminar la categoría', 'error');
      }
    }
  };

  const handleCancelNewCategory = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShowNewCategoryInput(false);
    setNewCategoryName('');
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEntity?.id) {
      // Si hay texto en la búsqueda pero no se seleccionó una entidad, preguntar si quiere crear una nueva
      if (entitySearch.trim()) {
        const result = await Swal.fire({
          title: 'Entidad no encontrada',
          html: `La entidad "<strong>${entitySearch}</strong>" no existe en el sistema.<br/><br/>¿Deseas crear una nueva entidad?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#0488AF',
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Sí, crear entidad',
          cancelButtonText: 'Cancelar',
        });

        if (result.isConfirmed && onOpenCreateEntity) {
          onOpenCreateEntity();
          onClose();
          return;
        }
      } else {
        Swal.fire('Selecciona una entidad', 'Debes seleccionar una entidad antes de crear la reunión.', 'warning');
      }
      return;
    }

    // Validar que la entidad seleccionada existe en la lista de entidades disponibles
    const entityExists = entityOptions.some(e => String(e.id) === String(selectedEntity.id));
    if (!entityExists) {
      // Si la entidad no existe, preguntar si quiere crear una nueva
      const result = await Swal.fire({
        title: 'Entidad no encontrada',
        html: `La entidad seleccionada no existe en el sistema.<br/><br/>¿Deseas crear una nueva entidad?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, crear entidad',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed && onOpenCreateEntity) {
        onOpenCreateEntity();
        onClose();
        return;
      }
      return;
    }

    // Validar que haya categorías disponibles
    const availableCategories = getCategories();
    if (!availableCategories || availableCategories.length === 0) {
      Swal.fire('Error', 'No hay categorías disponibles. Por favor, agrega al menos una categoría antes de crear la reunión.', 'error');
      return;
    }

    const f = new FormData(e.currentTarget);
    const date  = f.get('date');
    const title = (f.get('title') || '').toString().trim();
    const desc  = (f.get('desc')  || '').toString().trim();
    const categoryRaw = (f.get('category') || (availableCategories[0]?.value || 'OTRA')).toString();
    const location = (f.get('location') || '').toString().trim();

    if (!date) {
      Swal.fire('Falta fecha', 'Selecciona la fecha de la reunión.', 'info');
      return;
    }

    // Validar que la categoría sea un valor válido del enum del backend
    // Valores permitidos: ACERCAMIENTO, VINCULACION, PROYECTO, OTRA
    const validCategories = ['ACERCAMIENTO', 'VINCULACION', 'PROYECTO', 'OTRA'];
    const category = validCategories.includes(categoryRaw) ? categoryRaw : 'OTRA';

    const note = title && desc ? `${title} — ${desc}` : (title || desc || '');

    try {
      setBusy(true);
      
      // Mostrar modal de carga
      const loadingToast = Swal.fire({
        title: 'Creando reunión...',
        html: 'Por favor espera mientras se crea la reunión y se suben las minutas.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      // 1) Crear reunión por entityId
      const created = await otherCrmCreateMeeting({
        entityId: selectedEntity.id,
        date,
        note,
        title,
        description: desc,
        category,
        location,
        participants: meetingParticipants.length > 0 ? meetingParticipants : undefined,
        tags: meetingTags.length > 0 ? meetingTags : undefined,
      });
      const meeting = created?.data || created;

      // 2) Subir minutas por entityId
      const uploaded = await Promise.all(
        (files || []).map(file =>
          otherCrmUploadMinute({
            entityId: selectedEntity.id,
            title: file.name,
            date,
            file,
            meetingId: meeting?.id,
          }).then(r => r?.data || r)
        )
      );

      // Cerrar modal de carga
      loadingToast.close();

      await Swal.fire({
        icon: 'success',
        title: 'Listo',
        text: 'Reunión creada y minutas subidas.',
        timer: 1400,
        showConfirmButton: false,
      });

      onCreated?.(meeting, uploaded.filter(Boolean));
      // Disparar evento global para actualizar reuniones recientes
      window.dispatchEvent(new Event('meetingCreated'));
      onClose?.();
      
      // Reset form
      setSelectedEntity(null);
      setEntitySearch('');
      setFiles([]);
      setMeetingParticipants([]);
      setMeetingTags([]);
    } catch (err) {
      // Cerrar modal de carga en caso de error
      if (typeof loadingToast !== 'undefined' && loadingToast.close) {
        loadingToast.close();
      }
      Swal.fire('Error', err?.message || 'No fue posible crear la reunión', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setSelectedEntity(null);
      setEntitySearch('');
      setFiles([]);
      setMeetingParticipants([]);
      setMeetingTags([]);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="absolute inset-0 grid place-items-center p-6">
        <div className="w-full max-w-3xl rounded-2xl overflow-hidden bg-white ring-1 ring-black/5 flex flex-col max-h-[90vh]">
          {/* Header con gradiente estándar RDS */}
          <div
            className="px-5 py-4 text-white"
            style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-base sm:text-lg">Nueva reunión</div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body scrollable */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {/* Campo de entidad con autocompletar - ÚNICA DIFERENCIA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Entidad <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={searchInputRef}>
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={entitySearch}
                  onChange={(e) => {
                    setEntitySearch(e.target.value);
                    setShowEntityDropdown(true);
                    if (!e.target.value) {
                      setSelectedEntity(null);
                    }
                  }}
                  onFocus={() => setShowEntityDropdown(true)}
                  placeholder="Buscar entidad por nombre, región o comuna..."
                  className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                />
                {selectedEntity && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEntity(null);
                      setEntitySearch('');
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    title="Limpiar selección"
                  >
                    <X size={18} />
                  </button>
                )}
                
                {/* Dropdown de entidades */}
                {showEntityDropdown && filteredEntities.length > 0 && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
                  >
                    {filteredEntities.map((entity) => (
                      <button
                        key={entity.id}
                        type="button"
                        onClick={() => handleSelectEntity(entity)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{entity.name}</div>
                        {(entity.region || entity.district) && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {entity.region || ''}{entity.district ? ` / ${entity.district}` : ''}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedEntity && (
                <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm font-medium text-blue-900">{selectedEntity.name}</div>
                  {(selectedEntity.region || selectedEntity.district) && (
                    <div className="text-xs text-blue-700">
                      {selectedEntity.region || ''}{selectedEntity.district ? ` / ${selectedEntity.district}` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Fecha de la reunión <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="date"
                    name="date"
                    required
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Título <span className="text-gray-400 text-xs">(opcional)</span>
                </label>
                <input
                  name="title"
                  placeholder="Ej: Reunión de coordinación, Presentación de proyecto..."
                  title="Título breve que identifique la reunión"
                  className="w-full h-11 px-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Descripción / Acuerdos <span className="text-gray-400 text-xs">(opcional)</span>
              </label>
              <textarea
                name="desc"
                placeholder="Describe los temas tratados, acuerdos alcanzados, próximos pasos, etc."
                title="Información detallada sobre la reunión: temas tratados, acuerdos, compromisos, etc."
                rows={3}
                className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Categoría
                </label>
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      name="category"
                      defaultValue="OTRA"
                      className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                    >
                      {categories.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowCategoriesModal(true)}
                      className="h-11 px-2 flex items-center justify-center rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 hover:text-[#0488AF] transition-colors shrink-0"
                      title="Gestionar categorías"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-lg border-2 border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 shadow-sm mt-1.5">
                  <span className="text-sm leading-none">ℹ️</span>
                  <span>
                    Si no encuentras la categoría que buscas, créala en{" "}
                    <span className="font-bold text-blue-900">"Gestionar"</span>.
                  </span>
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Lugar <span className="text-gray-400 text-xs">(opcional)</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    name="location"
                    placeholder="Ej: Oficina principal, Sala de reuniones, Virtual (Zoom)..."
                    title="Dirección física o plataforma virtual donde se realizó la reunión"
                    className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                  />
                </div>
              </div>
            </div>


            {/* PARTICIPANTES / TAGS de REUNIÓN (chips con Enter) */}
            <div className="space-y-4">
              <div>
                <ChipsInput
                  icon={<Users className="w-4 h-4" />}
                  placeholder="Participantes de la reunión"
                  values={meetingParticipants}
                  onChange={setMeetingParticipants}
                  hint="Escribe el nombre de cada participante y presiona Enter para agregarlo. Estos participantes se asociarán a la reunión."
                />
              </div>
              <div>
                <ChipsInput
                  icon={<Tag className="w-4 h-4" />}
                  placeholder="Etiquetas de la reunión… (Enter)"
                  values={meetingTags}
                  onChange={setMeetingTags}
                  hint='Escribe etiquetas para categorizar la reunión (ej: "innovación", "convenio"). Presiona Enter para agregar cada etiqueta.'
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                <span className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Minutas <span className="text-gray-400 text-xs"></span>
                </span>
                <TooltipHelper text="Puedes seleccionar distintos formatos de archivos. Solo se aceptan archivos PDF." />
              </label>
              <input
                type="file"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  
                  // Validar tamaño de PDFs
                  for (const file of files) {
                    if (file.type === 'application/pdf' && file.size > 15 * 1024 * 1024) {
                      await Swal.fire({
                        icon: 'warning',
                        title: 'Archivo muy grande',
                        html: `El archivo "${file.name}" es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
                        confirmButtonText: 'Entendido',
                      });
                      e.target.value = '';
                      return;
                    }
                  }
                  
                  setFiles(files);
                }}
                accept=".pdf,application/pdf"
                className="mt-2 block w-full h-11 px-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#0488AF]/10 file:text-[#0488AF] hover:file:bg-[#0488AF]/20 cursor-pointer"
              />
              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700"
                    >
                      {f.name}
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                        className="ml-1 text-gray-500 hover:text-red-600"
                        aria-label="Quitar archivo"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-6 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-sm font-semibold transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crear reunión
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de gestión de categorías */}
      {showCategoriesModal && (
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCategoriesModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-2xl bg-white border shadow-xl overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Gestionar categorías</h3>
                </div>
                <button
                  onClick={() => setShowCategoriesModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Lista de categorías */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categorías existentes</label>
                  {categories.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {categories.map((c) => (
                        <div
                          key={c.value}
                          className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-700">{c.label}</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteCategory(c.value)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Eliminar categoría"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic py-4 text-center">No hay categorías. Crea una nueva abajo.</p>
                  )}
                </div>

                {/* Agregar nueva categoría */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agregar nueva categoría</label>
                  {!showNewCategoryInput ? (
                    <button
                      type="button"
                      onClick={() => setShowNewCategoryInput(true)}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0488AF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0c3b66] transition-colors"
                    >
                      <Plus size={16} />
                      Agregar nueva categoría
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCategory();
                          }
                          if (e.key === 'Escape') {
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }
                        }}
                        placeholder="Escribe el nombre y presiona Enter"
                        className="w-full h-10 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddCategory}
                          className="flex-1 h-10 px-4 rounded-lg bg-[#0488AF] text-white hover:bg-[#037a98] text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5"
                        >
                          <Plus className="w-4 h-4" />
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }}
                          className="h-10 px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

